import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Charity } from '@/types/charity';

export const useCharity = (ownerId?: string | null) => {
  const [charity, setCharity] = useState<Charity | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!ownerId) {
      setIsLoading(false);
      return;
    }

    const fetchCharity = async () => {
      try {
        setIsLoading(true);
        
        // Check session before making API call
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        if (sessionError) {
          console.error('Session error in useCharity:', sessionError);
          setError(new Error('Authentication session error'));
          return;
        }

        if (!session) {
          console.warn('No active session in useCharity');
          setCharity(null);
          return;
        }

        const { data, error } = await supabase
          .from('charities')
          .select('*')
          .eq('owner_id', ownerId)
          .maybeSingle();

        if (error) {
          console.error('Error fetching charity:', error);
          throw error;
        }

        setCharity(data || null);
      } catch (err) {
        console.error('Error fetching charity:', err);
        setError(err instanceof Error ? err : new Error('Unknown error occurred'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchCharity();
  }, [ownerId]);

  return { charity, isLoading, error, setCharity };
};
